import { useState, useRef, useEffect } from "react";

/**
 * Avatar button with a premium account dropdown: identity header + Profile,
 * Change password and Sign Out actions.
 */
function MemberMenu({ member, primary = "#0274F7", onProfile, onChangePassword, onAccountSettings, onSignOut }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const img = member?.img;
  const initial = (member?.name || "?").charAt(0).toUpperCase();

  const Avatar = ({ size }) => (
    <span
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold text-white"
      style={{ backgroundColor: primary, width: size, height: size, fontSize: size * 0.42 }}
    >
      {img ? <img src={img} alt="" className="h-full w-full object-cover" /> : initial}
    </span>
  );

  const itemCls =
    "flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left text-sm font-semibold transition";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-label="Account menu"
        className="group rounded-full transition-[transform,box-shadow] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform hover:-translate-y-0.5 hover:scale-105 focus:outline-none active:scale-95 active:duration-100"
        style={{
          // One consistent shadow shape (white ring → primary ring → glow) whose
          // colors/spread animate, so hover/active transition smoothly instead of snapping.
          boxShadow:
            open || hovered
              ? `0 0 0 2px #fff, 0 0 0 4px ${primary}, 0 10px 24px -8px ${primary}66`
              : `0 0 0 2px #fff, 0 0 0 4px ${primary}00, 0 2px 6px -2px rgba(0,0,0,0.22)`,
        }}
      >
        <span className="block overflow-hidden rounded-full transition duration-300 ease-out group-hover:brightness-105">
          <Avatar size={40} />
        </span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2.5 w-64 max-w-[calc(100vw-1rem)] overflow-hidden rounded-2xl bg-white shadow-[0_16px_48px_-12px_rgba(0,0,0,0.3)] ring-1 ring-black/5 z-[2000]">
          {/* identity header */}
          <div className="flex items-center gap-3 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white px-4 py-3.5">
            <Avatar size={44} />
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-slate-900">{member?.name || "Member"}</p>
              {member?.email && <p className="truncate text-xs text-slate-400">{member.email}</p>}
            </div>
          </div>

          {/* actions */}
          <div className="p-1.5">
            <button
              onClick={() => {
                setOpen(false);
                onProfile?.();
              }}
              className={`${itemCls} text-slate-700 hover:bg-slate-100`}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </span>
              Profile
            </button>
            <button
              onClick={() => {
                setOpen(false);
                onChangePassword?.();
              }}
              className={`${itemCls} text-slate-700 hover:bg-slate-100`}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </span>
              Change password
            </button>
            <button
              onClick={() => {
                setOpen(false);
                onAccountSettings?.();
              }}
              className={`${itemCls} text-slate-700 hover:bg-slate-100`}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </span>
              Account settings
            </button>
          </div>

          <div className="border-t border-slate-100 p-1.5">
            <button
              onClick={() => {
                setOpen(false);
                onSignOut?.();
              }}
              className={`${itemCls} text-red-600 hover:bg-red-50`}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500">
                <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12H3m0 0l4-4m-4 4l4 4M21 4v16a1 1 0 01-1 1h-6m7-18a1 1 0 00-1-1h-6" />
                </svg>
              </span>
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MemberMenu;
